import { For, Show } from "solid-js";

import { entityDetailLink } from "~/components/entityDetailLink";
import { useAgents, useAgentActions } from "~/lib/hooks";
import type { AgentActionRead } from "~/lib/types";

type Props = {
  wsKey: () => string;
};

/**
 * Workspace-wide feed of recent AI invocations. Workspace-member visible
 * (everyone in the workspace sees what AI ran lately — encourages
 * adoption and surfaces stuck/failed actions to people who can fix them).
 *
 * Distinct from the per-user "My recent changes" audit widget: that
 * shows things *you* changed, this shows what *AI* did regardless of
 * who triggered it.
 */
export function RecentAIActivityWidget(props: Props) {
  const actions = useAgentActions(props.wsKey, () => ({ limit: 10 }));
  const agents = useAgents(props.wsKey);

  // agent_id → display name. Avoids one row→one query.
  const agentName = (id: string): string => {
    const a = agents.data?.items.find((x) => x.id === id);
    return a?.name ?? id.slice(0, 14);
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <h3 class="card-title text-base">✨ Recent AI activity</h3>
        <p class="text-xs opacity-60 mt-0.5">Latest agent runs across this workspace.</p>

        <Show when={actions.isLoading}>
          <p class="text-sm opacity-60 mt-2">Loading…</p>
        </Show>

        <Show when={!actions.isLoading && (actions.data?.items.length ?? 0) === 0}>
          <p class="text-sm opacity-60 mt-2">
            No AI runs yet. Try "✨ Generate brief" on an account.
          </p>
        </Show>

        <ul class="mt-2 divide-y divide-base-200">
          <For each={actions.data?.items ?? []}>
            {(row) => <Row row={row} wsKey={props.wsKey} agentName={agentName} />}
          </For>
        </ul>
      </div>
    </div>
  );
}

function Row(props: {
  row: AgentActionRead;
  wsKey: () => string;
  agentName: (id: string) => string;
}) {
  // Subject link reuses the audit-log helper. Custom records resolve
  // their parent object key server-side (subject_object_key) so deep-
  // links work for every entity_type the helper knows about.
  const subject = () => {
    if (!props.row.subject_entity_type || !props.row.subject_entity_id) {
      return null;
    }
    return entityDetailLink(
      props.wsKey(),
      props.row.subject_entity_type,
      props.row.subject_entity_id,
      <span class="font-medium">
        {props.row.subject_entity_type} {props.row.subject_entity_id.slice(0, 8)}…
      </span>,
      props.row.subject_object_key,
    );
  };

  return (
    <li class="py-2 flex items-start gap-2 text-sm">
      <StateBadge state={props.row.state} />
      <div class="flex-1 min-w-0">
        <div class="truncate">
          <span class="opacity-70">{props.agentName(props.row.agent_id)}</span>{" "}
          <span class="opacity-50">·</span> <span class="font-mono text-xs">{props.row.kind}</span>
          <Show when={subject()}>
            <>
              {" "}
              <span class="opacity-50">on</span> {subject()}
            </>
          </Show>
        </div>
        <Show when={props.row.error}>
          <p class="text-xs text-error mt-0.5 truncate" title={props.row.error ?? ""}>
            {props.row.error}
          </p>
        </Show>
        <p class="text-xs opacity-50 mt-0.5">
          {relativeTime(props.row.started_at)}
          <Show when={props.row.model_used}>
            <span> · {props.row.model_used}</span>
          </Show>
          <Show when={props.row.input_tokens !== null || props.row.output_tokens !== null}>
            <span>
              {" "}
              · {props.row.input_tokens ?? "?"}↑{props.row.output_tokens ?? "?"}↓
            </span>
          </Show>
        </p>
      </div>
    </li>
  );
}

function StateBadge(props: { state: AgentActionRead["state"] }) {
  if (props.state === "running") {
    return <span class="badge badge-warning badge-sm shrink-0">running</span>;
  }
  if (props.state === "failed") {
    return <span class="badge badge-error badge-sm shrink-0">failed</span>;
  }
  return <span class="badge badge-success badge-sm shrink-0">ok</span>;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
